import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { OccupationDataOutput } from '@/graphql/generated';
import textsData from '@/textData/DashboardTexts.json';
import { useUserLanguage, useRentsThisMonthForDashboard, useErrorHandlerContext } from '@/customHooks';
import { theme } from '@/styles';
import { formatIncomeAmount } from '@/helpers/incomeThisMonth';
import { occupationPercentFromCounts } from '@/helpers/occupationPercent';
import { DashboardCardPeriodCaption } from '../atoms';

type DashboardOccupationCardProps = {
  data: OccupationDataOutput | undefined;
};

const OCCUPATION_PINK = '#C966B7';
const CHARGED_AMOUNT_COLOR = '#C015A2';
const COLORS = [theme.colors.primary1, OCCUPATION_PINK, '#e0e0e0'];

export const DashboardOccupationCard = ({ data }: DashboardOccupationCardProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es.occupationCard : textsData.en.occupationCard;
  const rentsTexts = texts.rentsSection ?? {
    title: 'Rents of this month',
    charged: 'Charged',
    notCharged: 'Not Charged',
    total: 'Total',
  };

  const trendTexts = texts.trendSection ?? {
    title: userLanguage === 'es' ? 'Tendencia de ocupación' : 'Occupation trend',
    vsLastMonth: userLanguage === 'es' ? 'vs mes pasado' : 'vs last month',
    metricNote: '',
    noData: userLanguage === 'es' ? 'Sin historial mensual' : 'No monthly history',
  };

  const { data: rentsData, error: rentsError } = useRentsThisMonthForDashboard();
  const { open: errorHandle } = useErrorHandlerContext();
  if (rentsError) errorHandle(rentsError);

  const convertedData = {
    occupiedQuantity: parseInt(data?.occupied ?? '0'),
    totalQuantity: parseInt(data?.total ?? '0'),
    freeQuantity: parseInt(data?.total ?? '0') - parseInt(data?.occupied ?? '0'),
  };

  const arrangedData = [
    { name: texts.labels.label1, value: convertedData.occupiedQuantity },
    { name: texts.labels.label2, value: convertedData.freeQuantity },
  ];

  const occupationPercentage = (): number => {
    if (convertedData.totalQuantity <= 0) return 0;
    return (convertedData.occupiedQuantity * 100) / convertedData.totalQuantity;
  };

  const charged = rentsData?.charged ?? 0;
  const notCharged = rentsData?.notCharged ?? 0;
  const rentsTotal = rentsData?.total ?? charged + notCharged;
  const chargedPct = rentsTotal > 0 ? (charged / rentsTotal) * 100 : 100;
  const notChargedPct = rentsTotal > 0 ? (notCharged / rentsTotal) * 100 : 0;

  const monthlyTrend = data?.monthlyTrend ?? [];
  const trendPercents = monthlyTrend.map((p) =>
    occupationPercentFromCounts(p.occupied, p.total),
  );

  const prevSnap = data?.previousMonth;
  let variationVsPrevMonth: number | null = null;
  if (
    prevSnap &&
    prevSnap.year != null &&
    prevSnap.month != null &&
    monthlyTrend.length > 0
  ) {
    const nextY = prevSnap.month === 12 ? prevSnap.year + 1 : prevSnap.year;
    const nextM = prevSnap.month === 12 ? 1 : prevSnap.month + 1;
    const currentHistorical = monthlyTrend.find(
      (p) => p.year === nextY && p.month === nextM,
    );
    if (currentHistorical) {
      variationVsPrevMonth =
        occupationPercentFromCounts(currentHistorical.occupied, currentHistorical.total) -
        occupationPercentFromCounts(prevSnap.occupied, prevSnap.total);
    }
  }
  if (variationVsPrevMonth === null && monthlyTrend.length >= 2) {
    const a = monthlyTrend[monthlyTrend.length - 2]!;
    const b = monthlyTrend[monthlyTrend.length - 1]!;
    variationVsPrevMonth =
      occupationPercentFromCounts(b.occupied, b.total) -
      occupationPercentFromCounts(a.occupied, a.total);
  }

  const variationDelta =
    variationVsPrevMonth != null && Number.isFinite(variationVsPrevMonth)
      ? variationVsPrevMonth
      : null;

  return (
    <div className="box-border h-full min-h-[480px] rounded-xl bg-white px-6 pb-6 pt-3 shadow-[0_14px_32px_rgba(15,23,42,0.16)]">
      <div>
        <h3 className="m-0 text-[20px] font-bold text-[#1d2f35]">{texts.title}</h3>
        <DashboardCardPeriodCaption />
        <hr className="mb-4 mt-[0.4rem] border-0 border-t border-t-[#E5E7EB]" />
      </div>
      <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-2">
        <div className="relative min-h-[220px] w-full">
          <div className="relative h-[220px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={arrangedData}
                  innerRadius={70}
                  outerRadius={96}
                  paddingAngle={1}
                  dataKey="value"
                >
                  {arrangedData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute left-1/2 top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2 text-[20px] font-bold text-gray-900">{`${occupationPercentage().toFixed(2)} %`}</div>
          </div>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 shrink-0 rounded-[25px]" style={{ backgroundColor: COLORS[0] }} />
            <p className="my-[6px] min-w-0 flex-1 text-base leading-[1.2] text-gray-500">{texts.labels.label1}</p>
            <p className="my-[6px] min-w-14 shrink-0 text-right text-[15px] font-bold leading-[1.2] text-gray-700">{convertedData.occupiedQuantity}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 shrink-0 rounded-[25px]" style={{ backgroundColor: COLORS[1] }} />
            <p className="my-[6px] min-w-0 flex-1 text-base leading-[1.2] text-gray-500">{texts.labels.label2}</p>
            <p className="my-[6px] min-w-14 shrink-0 text-right text-[15px] font-bold leading-[1.2] text-gray-700">{convertedData.freeQuantity}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 shrink-0 rounded-[25px]" style={{ backgroundColor: COLORS[2] }} />
            <p className="my-[6px] min-w-0 flex-1 text-base leading-[1.2] text-gray-500">{texts.labels.label3}</p>
            <p className="my-[6px] min-w-14 shrink-0 text-right text-[15px] font-bold leading-[1.2] text-gray-700">{convertedData.totalQuantity}</p>
          </div>
        </div>
      </div>

      <div className="mb-3 mt-5 flex items-center">
        <h4 className="m-0 font-[Montserrat,Arial,sans-serif] text-base font-bold text-[#545454]">{rentsTexts.title}</h4>
      </div>
      <div className="rounded-md bg-[#F8FAFC] px-5 py-4">
        <div className="flex items-center justify-between py-[0.6rem] text-base font-medium text-gray-600">
          <span>{rentsTexts.charged}</span>
          <span style={{ color: CHARGED_AMOUNT_COLOR }}>{formatIncomeAmount(charged)}</span>
        </div>
        <hr className="m-0 border-0 border-t border-t-[rgba(0,0,0,0.1)]" />
        <div className="flex items-center justify-between py-[0.6rem] text-base font-medium text-gray-600">
          <span>{rentsTexts.notCharged}</span>
          <span>{formatIncomeAmount(notCharged)}</span>
        </div>
        <hr className="m-0 border-0 border-t border-t-[rgba(0,0,0,0.1)]" />
        <div className="flex items-center justify-between pt-3 text-base font-bold text-gray-600">
          <span>{rentsTexts.total}</span>
          <span>{formatIncomeAmount(rentsTotal)}</span>
        </div>
      </div>
      <div className="mt-4 flex h-3 w-full overflow-hidden rounded-full bg-[rgba(0,0,0,0.06)]">
        <div style={{ width: `${chargedPct}%`, minWidth: chargedPct > 0 ? 2 : 0, backgroundColor: theme.colors.primary1 }} className="h-full transition-[width] duration-200 ease-in-out" />
        <div style={{ width: `${notChargedPct}%`, minWidth: notChargedPct > 0 ? 2 : 0, backgroundColor: OCCUPATION_PINK }} className="h-full transition-[width] duration-200 ease-in-out" />
      </div>
      <div className="mt-[1.15rem]">
        <div className="mb-2 flex items-baseline justify-between">
          <h5 className="m-0 text-sm font-bold text-gray-700">{trendTexts.title}</h5>
          {variationDelta != null ? (
            <span className={`text-[13px] font-bold ${variationDelta >= 0 ? 'text-[#4A9462]' : 'text-[#C015A2]'}`}>
              {variationDelta >= 0 ? '↑' : '↓'} {Math.abs(variationDelta).toFixed(1)}%{' '}
              {trendTexts.vsLastMonth}
            </span>
          ) : null}
        </div>
        {trendPercents.length === 0 ? (
          <p className="mb-0 ml-0 mr-0 mt-2 text-[13px] text-gray-500">{trendTexts.noData}</p>
        ) : (
          <div className="flex h-16 items-end gap-1.5 py-[0.35rem]">
            {trendPercents.map((value, index) => (
              <div
                key={`${monthlyTrend[index]!.year}-${monthlyTrend[index]!.month}-${index}`}
                className={`min-w-0 flex-1 rounded-[6px_6px_2px_2px] ${index === trendPercents.length - 1 ? 'bg-[#4A9462]' : 'bg-[rgba(74,148,98,0.25)]'}`}
                style={{ height: `${Math.max(10, Math.min(100, value))}%` }}
              />
            ))}
          </div>
        )}
        {trendTexts.metricNote ? <p className="mb-0 ml-0 mr-0 mt-[0.35rem] text-[11px] leading-[1.35] text-gray-500">{trendTexts.metricNote}</p> : null}
      </div>
    </div>
  );
};
